import type { ClientRecord as CoreRecord } from "../../../server/core/clients";
import { foldAllButtons, foldCards } from "../../ui/fold";
import type { ClientCareView } from "../../../server/modules/homecare/clienttypes";
import { openViewer } from "../../ui/viewer";
import {
  isClientModule,
  MODULE_LABEL,
  usDate,
  WAY_LABEL,
  WAYS,
  type ClientModule,
} from "../../../server/rules/clients";
import type { ScreenContext } from "../../shell/types";
import { messagesCard } from "../../ui/messages";
import { activityPanel } from "../../ui/activity";
import { autosaveStatus, createAutosave } from "../../ui/autosave";
import { api, el, toast } from "../../ui/dom";
import { icon } from "../../ui/icons";
import { openModal, type Modal } from "../../ui/modal";
import { card, chip, ib, muted, table } from "../../ui/record";
import { sendButton } from "../../ui/send";
import { uploadControl } from "../../ui/upload";
import { archiveModal, area, clientForm, endsCell, field, refuse, select } from "./forms";
import { claimSetupCard } from "../billing/claims";
import { billingRulesCard, clientBillingCard } from "../billing/client";
import { openBillRateCard } from "../billing/bill-rates";
import { seesBilling } from "../billing/patient";

/** The record, with Home Care's part (its client type and care rules) as the module keeps it. */
type ClientRecord = Omit<CoreRecord, "care"> & { care: ClientCareView | null };

// The client record (brief 1B-1, mockup client record and its "Who gets what" card): the stage
// of this module's onboarding, the details with the address and its map point, contracts with
// the signed file, sites or units, contacts, and who gets what. Edits happen in modals and save
// on their own a moment after the last change; every change is in the activity panel.

type View = ClientRecord;
type Res = {
  ok: boolean;
  status: number;
  body: View & {
    error?: string;
    flagged?: { level: "flag" | "block"; message: string } | null;
  };
};

const EYEBROW: Record<ClientModule, string> = {
  staffing: "Client",
  homecare: "Home care client",
  transport: "Transportation client",
};

/** A contract's name as the Send sheet and the Outbox show it. */
const contractTitle = (k: { number: string | null }) =>
  k.number ? `Contract ${k.number}` : "Signed contract";

export async function clientRecord(
  host: HTMLElement,
  ctx: ScreenContext,
  module: ClientModule,
  id: string,
) {
  const page = el("div", { class: "record" });
  const activity = activityPanel("client", id, { autosaved: true });
  // Brief 1E-32: every card on the record folds (its messages too), each person's choice kept.
  const wrap = el("div", { class: "recwrap" });
  wrap.append(page);
  // Brief 1E-32 (mockup v90): every card starts folded until the person opens it.
  foldCards(wrap, "client", { openFirst: [] });
  host.append(wrap);
  let view: View;

  const first = (await api<View>("GET", `/api/clients/${encodeURIComponent(id)}`)) as Res;
  if (!first.ok) {
    page.append(
      el(
        "div",
        { class: "blank", role: "alert" },
        icon("circle-slash", "big"),
        el("h2", {}, "Not available"),
        el("p", { class: "muted" }, "This client isn't here, or isn't used in a module that's on."),
      ),
    );
    return;
  }
  view = first.body;
  host.append(activity.el);
  // Text, email, call or note them from here; replies land here (brief 1B-5).
  page.after(messagesCard("client", id, () => void activity.refresh()));

  /** Takes a saved answer: redraws the record and the activity. */
  async function took(res: Res): Promise<Res> {
    if (res.ok) {
      view = res.body;
      if (page.isConnected) draw();
      await activity.refresh();
    }
    return res;
  }
  const send = async (method: string, path: string, body?: unknown) =>
    took((await api<View>(method, `/api/clients/${id}${path}`, body)) as Res);
  const reload = () => send("GET", "");

  /** An autosaving modal: saves `values()` with `put` a moment after each change. */
  function autosaving(m: Modal, inputs: HTMLElement[], put: () => Promise<Res>) {
    const status = autosaveStatus();
    m.root.querySelector(".cardhead")?.after(el("div", { style: "margin:-4px 0 4px" }, status.el));
    const saver = createAutosave({
      save: async () => {
        const res = await put();
        if (res.ok) m.note.hidden = true;
        else if (res.status === 400 || res.status === 409) refuse(m.note, res.body.error);
        return res.ok ? { ok: true } : { ok: false, error: res.body.error };
      },
      onStatus: status.show,
    });
    for (const i of inputs) {
      i.addEventListener("input", () => saver.changed());
      i.addEventListener("change", () => saver.changed());
    }
    return saver;
  }

  // ---- Home Care: the client's type and care rules (brief 1C-1, the Care rules stage) ---------------

  const putCare = async (b: unknown, done: string) => {
    const res = await send("PUT", "/care", b);
    if (!res.ok) toast("That wasn't saved", res.body.error);
    else toast(done);
  };

  /** Care rules, Visit sign-off and printed notes, and Rules: flag care, block money (mockup). */
  function careCards(edit: boolean): HTMLElement[] {
    const v = view.care;
    if (!v) return [];
    const r = v.careRules ?? v.fromType;
    const editType = () => {
      const pick = select(
        "Home Care client type",
        [["", "Pick a type"], ...v.types.map((t) => [t.id, t.name] as const)],
        v.clientTypeId ?? "",
      );
      const hours = field(
        "Response window (hours; empty: per plan)",
        r?.responseHours == null ? "" : String(r.responseHours),
        { type: "number", min: "1", max: "720" },
      );
      const needed = el("input", {
        type: "checkbox",
        id: "cr-auth",
        checked: !!r?.authorization.needed,
      });
      const words = field("Authorizations, as they show", r?.authorization.words ?? "", {
        maxlength: "80",
      });
      const save = el("button", { type: "button", class: "btn pri" }, "Save");
      const m = openModal({
        title: "Care rules",
        sub: "From the client type, then changed for this client. Changing the type copies its rules again.",
        body: [
          el(
            "div",
            { class: "form", style: "max-width:none" },
            pick.el,
            hours.el,
            el(
              "label",
              { for: "cr-auth", style: "display:flex;gap:6px;align-items:center" },
              needed,
              "Authorizations needed",
            ),
            words.el,
          ),
        ],
        actions: [save],
      });
      save.addEventListener("click", async () => {
        save.disabled = true;
        const res = await send("PUT", "/care", {
          ...(pick.input.value && pick.input.value !== v.clientTypeId
            ? { clientTypeId: pick.input.value }
            : {}),
          rules: {
            responseHours: hours.input.value === "" ? null : Number(hours.input.value),
            authorization: { needed: needed.checked, words: words.input.value },
          },
        });
        save.disabled = false;
        if (!res.ok) return refuse(m.note, res.body.error);
        m.close();
        toast("Care rules saved");
      });
    };
    const rulesCard = card(
      "Care rules",
      v.clientTypeName
        ? `From ${v.clientTypeName}, set for this client`
        : "Pick the client's type first",
      edit ? [ib("pencil", "Edit care rules", editType)] : [],
      r
        ? el(
            "dl",
            { class: "kv" },
            el("dt", {}, "Type"),
            el("dd", {}, v.clientTypeName ?? "–"),
            el("dt", {}, "Response window"),
            el("dd", {}, `${r.responseWords} referral to visit`),
            el("dt", {}, "Authorizations"),
            el("dd", {}, r.authorization.words),
          )
        : muted("No type yet: its care rules come with it."),
    );
    if (!r) return [el("div", { class: "grid2" }, rulesCard)];
    const sign = select(
      "How a visit is signed off",
      v.signOffs.map((o) => [o.value, o.label] as const),
      r.signOff,
    );
    const printed = select(
      "Printed visit notes show",
      v.printedNotes.map((o) => [o.value, o.label] as const),
      r.printedNote,
    );
    for (const [s, k] of [
      [sign, "signOff"],
      [printed, "printedNote"],
    ] as const) {
      s.input.disabled = !edit;
      s.input.addEventListener(
        "change",
        () => void putCare({ rules: { [k]: s.input.value } }, "Saved"),
      );
    }
    // Brief 1E-18 (mockup v78): what notes and invoices show, and what goes with each invoice.
    const withInvoice = select(
      "With each invoice",
      v.notesWithInvoiceChoices.map((o) => [o.value, o.label] as const),
      v.notesWithInvoice,
    );
    withInvoice.input.disabled = !edit;
    withInvoice.input.addEventListener(
      "change",
      () => void putCare({ notesWithInvoice: withInvoice.input.value }, "Saved"),
    );
    // Brief 1E-30: an orientation visit is billed only when this is on.
    const billOrientation = el("input", {
      type: "checkbox",
      checked: v.billOrientation,
      disabled: !edit,
    });
    billOrientation.addEventListener(
      "change",
      () =>
        void putCare(
          { billOrientation: billOrientation.checked },
          billOrientation.checked ? "Orientation is billed" : "Orientation isn't billed",
        ),
    );
    const shows = el(
      "div",
      { class: "field" },
      el("label", {}, "Notes and invoices show"),
      el("span", { class: "small" }, v.notesShow),
    );
    const signCard = card(
      el("span", {}, icon("pencil"), " Visit sign-off and printed notes"),
      "Set once for this client; every visit follows it",
      [],
      el(
        "div",
        { class: "form", style: "max-width:none" },
        sign.el,
        printed.el,
        shows,
        withInvoice.el,
        el("label", { class: "ptask" }, billOrientation, " Bill orientation"),
      ),
    );
    const money = card(
      el("span", {}, icon("shield-check"), " Rules: flag care, hold bill or pay"),
      "What holds the bill or the pay for this client.",
      [],
      table(
        [
          { h: "When", f: (x) => x.label },
          { h: "Care", f: () => chip("Flag", "warn") },
          {
            h: "Bill or pay",
            f: (x) => {
              const s = el(
                "select",
                { "aria-label": `Bill or pay: ${x.label}`, disabled: !edit },
                ...v.levels.map((l) =>
                  el(
                    "option",
                    {
                      value: l.value,
                      selected: r.money[x.key as keyof typeof r.money] === l.value,
                    },
                    l.label,
                  ),
                ),
              );
              s.addEventListener(
                "change",
                () => void putCare({ rules: { money: { [x.key]: s.value } } }, "Rule saved"),
              );
              return s;
            },
          },
        ],
        v.rules,
        undefined,
        "Rules: flag care, hold bill or pay",
      ),
    );
    return [
      el("div", { class: "grid2" }, rulesCard),
      el("div", { class: "grid2" }, signCard, money),
    ];
  }

  // ---- The client's details ---------------------------------------------------------------------
  function editClient() {
    const on = ctx.me.modulesOn.filter(isClientModule);
    const c = view.client;
    const form = clientForm(module, on, {
      name: c.name,
      kind: c.kind,
      clientType: c.clientType,
      types: c.typeChoices[module] ?? [],
      modules: c.modules,
      address: c.address,
      ...(module === "homecare" && view.care
        ? { homecareTypes: view.care.types, homecareTypeId: c.homecareTypeId }
        : {}),
    });
    const m = openModal({
      title: `Edit ${c.name}`,
      sub: "Changes save as they're made.",
      body: [el("div", { class: "form", style: "max-width:none" }, form.el)],
      wide: true,
    });
    form.focus();
    autosaving(m, form.inputs, () => send("PUT", "", form.values()));
  }

  // ---- Contracts -----------------------------------------------------------------------------------
  type Contract = View["contracts"][number];
  function contractFields(k?: Contract) {
    const number = field("Contract number", k?.number, { maxlength: "60" });
    // The live businesses, starting at the one this module runs under (brief 1E-12).
    const moduleBiz = (view.moduleBusiness as Record<string, string | null>)[module] ?? null;
    const isLive = (id: string | null | undefined) =>
      !!id && view.businesses.some((b) => b.id === id);
    const chosen = k
      ? isLive(k.businessId)
        ? k.businessId
        : null
      : isLive(moduleBiz)
        ? moduleBiz
        : null;
    const business = select(
      "Business",
      [
        ...(chosen ? [] : ([["", "–"]] as const)),
        ...view.businesses.map((b) => [b.id, b.name] as const),
      ],
      chosen,
    );
    business.el.append(
      el("span", { class: "hint" }, "Invoices and claims for this contract go out under it."),
    );
    const starts = field("Starts", k?.startsOn, { type: "date" });
    const ends = field("Ends", k?.endsOn, { type: "date" });
    const terms = area("Terms", k?.terms);
    // Payment terms in net days (brief 1E-1): the due date is the invoice date plus these.
    const net = field("Payment terms (net days)", String(k?.termsDays ?? 30), {
      type: "number",
      min: "0",
      max: "365",
      inputmode: "numeric",
    });
    const grid = el(
      "div",
      { class: "formgrid" },
      number.el,
      business.el,
      starts.el,
      ends.el,
      el(
        "p",
        { class: "hint wide", style: "margin:-4px 0 0" },
        "Leave the end empty for a contract that doesn't end.",
      ),
      net.el,
      terms.el,
    );
    return {
      grid,
      inputs: [number.input, business.input, starts.input, ends.input, net.input, terms.input],
      values: () => ({
        number: number.input.value,
        businessId: business.input.value || null,
        startsOn: starts.input.value || null,
        endsOn: ends.input.value || null,
        terms: terms.input.value,
        termsDays: net.input.value,
      }),
    };
  }

  function addContract() {
    const f = contractFields();
    const add = el("button", { type: "button", class: "btn pri" }, icon("plus"), " Add contract");
    const m = openModal({
      title: "Add a contract",
      sub: "Dates and terms. Attach the signed contract once it's added.",
      body: [el("div", { class: "form", style: "max-width:none" }, f.grid)],
      actions: [add],
      wide: true,
    });
    f.inputs[0]?.focus();
    add.addEventListener("click", async () => {
      add.disabled = true;
      const had = new Set(view.contracts.map((k) => k.id));
      const res = await send("POST", "/contracts", f.values());
      add.disabled = false;
      if (!res.ok) return refuse(m.note, res.body.error);
      m.close();
      const made = view.contracts.find((k) => !had.has(k.id));
      if (made) editContract(made);
    });
  }

  function editContract(k: Contract) {
    const f = contractFields(k);
    const signed = el("div", { class: "field wide" });
    const m = openModal({
      title: k.number ? `Contract ${k.number}` : "Contract",
      sub: "Changes save as they're made.",
      body: [el("div", { class: "form", style: "max-width:none" }, f.grid, signed)],
      wide: true,
    });
    const drawSigned = () => {
      const now = view.contracts.find((x) => x.id === k.id);
      const file = now?.signedFile;
      const upload = uploadControl({
        kind: "client",
        id,
        label: file ? "Replace signed contract" : "Attach signed contract",
        onUploaded: async (up) => {
          const res = await send("PUT", `/contracts/${k.id}`, { signedFileId: up.id });
          if (!res.ok) return refuse(m.note, res.body.error);
          toast("Signed contract attached", up.name);
          drawSigned();
        },
      });
      const getIt = file
        ? el("button", { type: "button", class: "btn sm" }, icon("eye"), " ", file.name)
        : null;
      getIt?.addEventListener(
        "click",
        () => file && void openViewer({ file: { id: file.id, name: file.name } }),
      );
      signed.replaceChildren(
        el("span", { class: "label" }, "Signed contract"),
        el(
          "div",
          { class: "row" },
          getIt ?? muted("Not attached yet"),
          file
            ? sendButton({
                files: [file],
                document: contractTitle(k),
                for: "Contract and rates",
              })
            : null,
        ),
        upload.el,
      );
    };
    drawSigned();
    autosaving(m, f.inputs, () => send("PUT", `/contracts/${k.id}`, f.values()));
    const arch = el("button", { type: "button", class: "btn sm ghost" }, "Archive contract");
    arch.addEventListener("click", () => {
      m.close();
      archiveModal("contract", `/api/clients/${id}/contracts/${k.id}/archive`, reload);
    });
    m.root.append(el("div", { class: "actions" }, arch));
  }

  // ---- Contacts ------------------------------------------------------------------------------------
  type Contact = View["contacts"][number];
  function contactFields(c?: Contact) {
    const name = field("Name", c?.name, { maxlength: "120" });
    const title = field("Title", c?.title, { maxlength: "80" });
    const email = field("Email", c?.email, { type: "email", maxlength: "160" });
    const phone = field("Phone", c?.phone, { type: "tel", maxlength: "30" });
    const fax = field("Fax", c?.fax, { type: "tel", maxlength: "30" });
    const notes = area("Notes", c?.notes);
    return {
      grid: el(
        "div",
        { class: "formgrid" },
        name.el,
        title.el,
        email.el,
        phone.el,
        fax.el,
        notes.el,
      ),
      inputs: [name.input, title.input, email.input, phone.input, fax.input, notes.input],
      values: () => ({
        name: name.input.value,
        title: title.input.value,
        email: email.input.value,
        phone: phone.input.value,
        fax: fax.input.value,
        notes: notes.input.value,
      }),
    };
  }

  function addContact() {
    const f = contactFields();
    const add = el("button", { type: "button", class: "btn pri" }, icon("plus"), " Add contact");
    const m = openModal({
      title: "Add a contact",
      sub: `Someone at ${view.client.name}.`,
      body: [el("div", { class: "form", style: "max-width:none" }, f.grid)],
      actions: [add],
      wide: true,
    });
    f.inputs[0]?.focus();
    add.addEventListener("click", async () => {
      add.disabled = true;
      const res = await send("POST", "/contacts", f.values());
      add.disabled = false;
      if (!res.ok) return refuse(m.note, res.body.error);
      m.close();
      toast(`Contact added: ${f.values().name.trim()}`);
    });
  }

  function editContact(c: Contact) {
    const f = contactFields(c);
    const m = openModal({
      title: c.name,
      sub: "Changes save as they're made.",
      body: [el("div", { class: "form", style: "max-width:none" }, f.grid)],
      wide: true,
    });
    autosaving(m, f.inputs, () => send("PUT", `/contacts/${c.id}`, f.values()));
    const arch = el("button", { type: "button", class: "btn sm ghost" }, "Archive contact");
    arch.addEventListener("click", () => {
      m.close();
      archiveModal("contact", `/api/clients/${id}/contacts/${c.id}/archive`, reload);
    });
    m.root.append(el("div", { class: "actions" }, arch));
  }

  // ---- Who gets what -------------------------------------------------------------------------------
  type Who = View["who"][number]["rows"][number];
  function changeWho(r: Who) {
    const people = view.contacts.filter((c) => !c.archived);
    const person = select(
      "Person",
      [["", "Nobody"], ...people.map((c) => [c.id, c.name] as const)],
      r.contactId,
    );
    // Every way they get it, at least one (brief 1E-15, mockup v65).
    const had = r.ways.length ? r.ways.map((w) => w.value) : ["email"];
    const boxes = WAYS.map((w) => ({
      way: w,
      box: el("input", { type: "checkbox", value: w, checked: had.includes(w) }),
    }));
    const how = el(
      "fieldset",
      { class: "tsf wide" },
      el("legend", {}, "How they get it"),
      el(
        "div",
        { class: "tsgrid" },
        ...boxes.map((b) => el("label", { class: "tsr" }, b.box, ` ${WAY_LABEL[b.way]}`)),
      ),
      el(
        "span",
        { class: "small muted" },
        "A portal link opens one document and expires; a portal account sees everything they handle.",
      ),
    );
    const said = el("p", { class: "small", role: "status" }, r.message);
    const m = openModal({
      title: `Who handles ${r.name}`,
      sub: r.gets ? `Gets automatically: ${r.gets}.` : undefined,
      body: [
        el("div", { class: "formgrid" }, person.el, how, el("div", { class: "wide" }, said)),
        ...(people.length
          ? []
          : [el("p", { class: "note" }, "Add a contact first, then pick them here.")]),
      ],
      wide: true,
    });
    person.input.focus();
    autosaving(m, [person.input, ...boxes.map((b) => b.box)], async () => {
      const res = await send("PUT", "/who", {
        responsibilityId: r.id,
        contactId: person.input.value || null,
        channels: boxes.filter((b) => b.box.checked).map((b) => b.way),
      });
      const now = res.ok
        ? res.body.who.flatMap((w) => w.rows).find((x) => x.id === r.id)
        : undefined;
      if (now) said.textContent = now.message;
      return res;
    });
  }

  function addResponsibility() {
    const name = field("Responsibility", "", { maxlength: "60" });
    const gets = field("Gets automatically", "", { maxlength: "120" });
    const add = el("button", { type: "button", class: "btn pri" }, icon("plus"), " Add");
    const m = openModal({
      title: `Add a ${MODULE_LABEL[module]} responsibility`,
      sub: "The company's own, on every client this module uses.",
      body: [el("div", { class: "formgrid" }, name.el, gets.el)],
      actions: [add],
    });
    name.input.focus();
    add.addEventListener("click", async () => {
      add.disabled = true;
      const res = await api("POST", "/api/responsibilities", {
        module,
        name: name.input.value,
        gets: gets.input.value,
      });
      add.disabled = false;
      if (!res.ok) return refuse(m.note, res.body.error);
      m.close();
      await reload();
      toast(`${name.input.value.trim()} added to Who gets what`);
    });
  }

  // ---- Drawing ---------------------------------------------------------------------------------------
  function draw() {
    const c = view.client;
    const edit = view.canEdit && !c.archived;
    const used = c.modules.includes(module);
    ctx.crumb(c.name);

    const headActions: Node[] = [];
    if (edit) {
      const e = el("button", { type: "button", class: "btn" }, icon("pencil"), " Edit");
      e.addEventListener("click", editClient);
      const a = el("button", { type: "button", class: "btn" }, "Archive");
      a.addEventListener("click", () =>
        archiveModal("client", `/api/clients/${id}/archive`, reload),
      );
      headActions.push(e, a);
    }
    const head = el(
      "div",
      { class: "head" },
      el(
        "div",
        { class: "t" },
        el("div", { class: "eyebrow" }, EYEBROW[module]),
        el("h1", {}, icon("organization", "h1i"), c.name),
        el(
          "div",
          { class: "muted" },
          [
            module === "homecare" ? (view.care?.clientTypeName ?? c.clientType) : c.clientType,
            c.kindLabel,
          ]
            .filter(Boolean)
            .join(" · "),
        ),
      ),
      el("div", { class: "actions" }, ...headActions, ...foldAllButtons(wrap)),
    );

    // Stage of this module's onboarding (mockup stageCard).
    let stage: HTMLElement | null = null;
    if (used) {
      const stages = c.stageNames[module] ?? [];
      const cur = c.stages[module] ?? 0;
      const next = stages[cur + 1];
      const move = (way: "next" | "back") => async () => {
        const res = await send("POST", "/stage", { module, way });
        if (!res.ok) toast("The stage didn't change", res.body.error);
        // Leaving a stage with something missing moves on and flags it (brief 1B-7).
        else if (res.body.flagged)
          toast(
            res.body.flagged.level === "block"
              ? "Moved on · blocked, bill or pay held"
              : "Moved on · flagged",
            res.body.flagged.message,
          );
      };
      const back = edit && cur > 0 ? ib("arrow-right", "Step back", move("back")) : null;
      back?.classList.add("flip");
      const on =
        edit && next
          ? el("button", { type: "button", class: "btn pri" }, `Complete · move to ${next}`)
          : null;
      on?.addEventListener("click", move("next"));
      stage = el(
        "section",
        { class: "card stagebox", "aria-label": "Stage" },
        el(
          "div",
          { class: "cardhead", style: "margin:0" },
          el(
            "div",
            {},
            el("div", { class: "eyebrow" }, `Stage ${cur + 1} of ${stages.length}`),
            el("h2", {}, stages[cur] ?? ""),
          ),
          back,
        ),
        el(
          "ol",
          { class: "stepper" },
          ...stages.map((s, i) =>
            el(
              "li",
              { class: i < cur ? "done" : i === cur ? "cur" : undefined },
              el("span", { class: "bar" }),
              el("span", { class: "lab" }, el("span", { class: "n" }, String(i + 1)), s),
            ),
          ),
        ),
        el("div", { class: "nextstep" }, el("p", {}, c.stageHints[module] ?? ""), on),
      );
    }

    // Details: kind, type, modules, address and its map point.
    const details = card(
      "Details",
      null,
      edit ? [ib("pencil", "Edit details", editClient)] : [],
      el(
        "dl",
        { class: "kv" },
        el("dt", {}, "Kind"),
        el("dd", {}, c.kindLabel),
        el("dt", {}, "Type"),
        el(
          "dd",
          {},
          (module === "homecare" ? view.care?.clientTypeName : null) ?? c.clientType ?? "–",
        ),
        el("dt", {}, "Used in"),
        el("dd", {}, c.modules.map((m) => MODULE_LABEL[m]).join(", ")),
        el("dt", {}, "Address"),
        el("dd", {}, c.addressLine ?? "No address yet"),
        el("dt", {}, "Map"),
        el("dd", { "data-map": c.map.status }, chip(c.map.label, c.map.level)),
      ),
    );

    // Contracts with dates, terms and the signed file.
    const contracts = card(
      "Contracts",
      null,
      edit ? [ib("plus", "Add a contract", addContract)] : [],
      view.contracts.length
        ? table(
            [
              {
                h: "Contract",
                f: (k) =>
                  el(
                    "span",
                    {},
                    el("b", {}, k.number ? `No. ${k.number}` : "No number"),
                    k.businessName ? el("div", { class: "small muted" }, k.businessName) : null,
                  ),
              },
              {
                h: "Term",
                f: (k) =>
                  `${k.startsOn ? usDate(k.startsOn) : "–"} – ${k.endsOn ? usDate(k.endsOn) : "Evergreen"}`,
              },
              {
                h: "Ends",
                f: (k) =>
                  k.archived
                    ? chip("Archived")
                    : endsCell(k.endsOn ? usDate(k.endsOn) : "Evergreen", k.warning),
              },
              {
                h: "Signed",
                f: (k) => {
                  const file = k.signedFile;
                  if (!file) return muted("Not attached");
                  const b = el(
                    "button",
                    { type: "button", class: "btn sm ghost" },
                    icon("file"),
                    " ",
                    file.name,
                  );
                  b.addEventListener(
                    "click",
                    () => void openViewer({ file: { id: file.id, name: file.name } }),
                  );
                  return el(
                    "span",
                    { class: "filerow" },
                    b,
                    sendButton({
                      files: [file],
                      document: contractTitle(k),
                      for: "Contract and rates",
                    }),
                  );
                },
              },
              // Brief 1E-16: the contract's bill rate card (made from the standard prices when none).
              ...(module === "homecare" && seesBilling(ctx)
                ? [
                    {
                      h: "",
                      num: true,
                      f: (k: (typeof view.contracts)[number]) => {
                        if (k.archived) return "";
                        const b = el(
                          "button",
                          { type: "button", class: "btn sm" },
                          icon("tag"),
                          " Bill rate card",
                        );
                        b.addEventListener("click", () => void openBillRateCard(k.id, ctx.go));
                        return b;
                      },
                    },
                  ]
                : []),
            ],
            view.contracts,
            edit ? (k) => (k.archived ? undefined : editContract(k)) : undefined,
            "Contracts",
          )
        : el("p", { class: "muted" }, "No contract yet."),
    );

    // Sites or units.
    const unitName = field("Name", "", { maxlength: "120", placeholder: "4 West, Main campus" });
    const unitKind = select(
      "Kind",
      [
        ["site", "Site"],
        ["unit", "Unit"],
      ] as const,
      "site",
    );
    const addUnit = el("button", { type: "submit", class: "btn sm" }, icon("plus"), " Add");
    const unitForm = el(
      "form",
      {
        class: "formgrid",
        style: "margin-top:12px;align-items:end",
        "aria-label": "Add a site or unit",
      },
      unitName.el,
      el("div", { class: "row", style: "align-items:end;gap:8px" }, unitKind.el, addUnit),
    );
    unitForm.addEventListener("submit", async (e) => {
      e.preventDefault();
      addUnit.disabled = true;
      const res = await send("POST", "/units", {
        name: unitName.input.value,
        kind: unitKind.input.value,
      });
      addUnit.disabled = false;
      if (!res.ok) toast("That wasn't added", res.body.error);
    });
    const units = card(
      "Sites and units",
      null,
      [],
      view.units.length
        ? table(
            [
              { h: "Name", f: (u) => el("b", {}, u.name) },
              {
                h: "Kind",
                f: (u) => (u.archived ? chip("Archived") : u.kind === "unit" ? "Unit" : "Site"),
              },
              {
                h: "",
                num: true,
                f: (u) =>
                  edit && !u.archived
                    ? ib("x", `Archive ${u.name}`, () =>
                        archiveModal(u.name, `/api/clients/${id}/units/${u.id}/archive`, reload),
                      )
                    : "",
              },
            ],
            view.units,
            undefined,
            "Sites and units",
          )
        : el("p", { class: "muted" }, "No sites or units yet."),
      ...(edit ? [unitForm] : []),
    );

    // Contacts.
    const contacts = card(
      "Contacts",
      null,
      edit ? [ib("plus", "Add a contact", addContact)] : [],
      view.contacts.length
        ? table(
            [
              {
                h: "Name",
                f: (p) =>
                  el(
                    "span",
                    {},
                    el("b", {}, p.name),
                    p.title ? el("div", { class: "small muted" }, p.title) : null,
                  ),
              },
              {
                h: "Email and phone",
                f: (p) =>
                  p.email || p.phone
                    ? el(
                        "span",
                        {},
                        p.email ?? "",
                        p.phone ? el("div", { class: "small muted" }, p.phone) : null,
                      )
                    : "–",
              },
              {
                h: "Gets",
                f: (p) =>
                  p.archived
                    ? chip("Archived")
                    : el("span", { class: "chips" }, ...p.gets.map((g) => chip(g))),
              },
            ],
            view.contacts,
            edit ? (p) => (p.archived ? undefined : editContact(p)) : undefined,
            "Contacts",
          )
        : el("p", { class: "muted" }, "No contacts yet."),
    );

    // Who gets what, for this module (mockup contactsCard; v65: every way as a chip, and the
    // warning when Billing or Payments has nobody).
    const mine = view.who.find((w) => w.module === module);
    const rows = mine?.rows ?? [];
    const who = el(
      "section",
      { class: "card", "aria-label": "Who gets what" },
      el(
        "div",
        { class: "cardhead" },
        el(
          "div",
          {},
          el("h2", {}, icon("people"), " Who gets what"),
          el(
            "span",
            { class: "small muted" },
            "Messages and documents go to the person responsible",
          ),
        ),
        edit ? ib("plus", "Add a responsibility", addResponsibility) : el("span"),
      ),
      used && mine?.nobody
        ? el(
            "div",
            {
              class: "note",
              role: "status",
              style: "margin-bottom:8px;border-left-color:var(--warn)",
            },
            el("b", {}, mine.nobody),
            " Invoices and payment questions for this client have no one to go to.",
          )
        : null,
      used
        ? table(
            [
              { h: "Responsibility", f: (r) => el("b", {}, r.name) },
              {
                h: "Person",
                f: (r) =>
                  r.contactName
                    ? el(
                        "span",
                        {},
                        r.contactName,
                        r.ok ? null : el("div", { class: "small" }, chip(r.message, "warn")),
                      )
                    : muted("Nobody set"),
              },
              { h: "Gets automatically", f: (r) => el("span", { class: "small" }, r.gets) },
              {
                h: "How",
                f: (r) =>
                  r.contactName && r.ways.length
                    ? el("span", { class: "chips" }, ...r.ways.map((w) => chip(w.label)))
                    : "–",
              },
              {
                h: "",
                num: true,
                f: (r) =>
                  edit ? ib("pencil", `Change who handles ${r.name}`, () => changeWho(r)) : "",
              },
            ],
            rows,
            undefined,
            "Who gets what",
          )
        : el("p", { class: "muted" }, `This client isn't used in ${MODULE_LABEL[module]}.`),
    );

    const care = module === "homecare" && used ? careCards(edit) : [];
    // Billing (brief 1E-1): terms, format and the current rates; margin with the Money switch.
    if (module === "homecare" && used && seesBilling(ctx)) {
      // Brief 1E-35: the Billing rules card (mockup v93), above Billing.
      const rules = el("div");
      void billingRulesCard(rules, id);
      care.push(rules);
      const slot = el("div");
      // The CMS-1500 setup (brief 1E-2): set up once for a client billed by claim form; shown
      // only while the client bills by invoice and CMS-1500 (brief 1E-15).
      const claims = el("div");
      void clientBillingCard(slot, id, () => void claimSetupCard(claims, id, ctx));
      care.push(slot);
      void claimSetupCard(claims, id, ctx);
      care.push(claims);
    }
    page.replaceChildren(
      head,
      ...(c.archived
        ? [el("p", { class: "note", role: "status" }, `Archived: ${c.archiveReason ?? ""}`)]
        : []),
      ...(stage ? [stage] : []),
      el("div", { class: "recgrid" }, details, contracts, units, contacts),
      ...care,
      who,
    );
  }

  draw();
}
